// Phone inbox (2026-10-25): pure checks and math for functions/api/inbox.js, so they can be
// tested without a server. Three things arrive from Kenny's iPhone shortcuts:
//   quick  - a lock-screen tap: calories (+ optional note) eaten now
//   sync   - after the iHealth or MyFitnessPal app closes: the latest scale reading from
//            Apple Health and/or that day's MyFitnessPal total
//   left   - the lock-screen widget asking how many calories are left today
import { bookPlan } from './nutrition.js';

const ISO = /^\d{4}-\d{2}-\d{2}$/;

// The phone sends its own local date; this is only the fallback (Kenny is in Georgia).
export function localDate(now = new Date(), tz = 'America/New_York') {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

function num(v, lo, hi) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[^0-9.\-]/g, '')); // Shortcuts may send "208.6 lb"
  return Number.isFinite(n) && n >= lo && n <= hi ? Math.round(n * 10) / 10 : undefined;
}
const dateOr = (d, fallback) => (typeof d === 'string' && ISO.test(d.trim()) ? d.trim() : fallback);

export function parseQuick(body = {}, today = localDate()) {
  const calories = num(body.calories, 1, 5000);
  if (calories == null || calories === undefined) return { error: 'calories must be a number from 1 to 5000' };
  const note = String(body.note ?? '').replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, 80);
  return { calories: Math.round(calories), note: note || null, date: dateOr(body.date, today) };
}

// Apple Health keeps body fat as a fraction (0.241); some exports send 24.1. Accept both.
function fatPct(v, errors) {
  const n = num(v, 0, 100);
  if (n === null) return null;
  const raw = Number(String(v).replace(/[^0-9.\-]/g, ''));
  const pct = raw > 0 && raw < 1 ? Math.round(raw * 1000) / 10 : n;
  if (n === undefined || pct < 1 || pct > 75) { errors.push('body_fat_pct'); return null; }
  return pct;
}

// Every field is optional; a bad value is dropped (and reported) instead of failing the sync.
export function parseSync(body = {}, today = localDate()) {
  const errors = [];
  const pick = (k, lo, hi) => { const v = num(body[k], lo, hi); if (v === undefined) { errors.push(k); return null; } return v; };
  const weight = {
    weight_lb: pick('weight_lb', 50, 700),
    body_fat_pct: fatPct(body.body_fat_pct, errors),
    lean_mass_lb: pick('lean_mass_lb', 30, 500),
    bmi: pick('bmi', 8, 90),
  };
  const hasWeight = Object.values(weight).some((v) => v != null);
  const food = {
    calories: pick('food_calories', 0, 20000),
    protein_g: pick('food_protein_g', 0, 2000),
    carbs_g: pick('food_carbs_g', 0, 3000),
    fat_g: pick('food_fat_g', 0, 1000),
  };
  const hasFood = food.calories != null;
  return {
    weight: hasWeight ? { date: dateOr(body.weight_date, today), ...weight } : null,
    food: hasFood ? { date: dateOr(body.food_date, today), ...food } : null,
    errors,
  };
}

// Calories left today, the Food tab's way (book calories from the newest weigh-in).
export function caloriesLeft({ latestWeightLb, settings = {}, eaten = 0, date }) {
  const dietBreak = !!settings.diet_break_until && date <= settings.diet_break_until && (settings.diet_goal || 'cut') === 'cut';
  const plan = bookPlan({ weightLb: latestWeightLb, goal: settings.diet_goal || 'cut', activity: settings.activity_level || 'moderate', dietBreak });
  if (!plan.ready) return { ready: false, eaten: Math.round(eaten) };
  return { ready: true, target: plan.calories, eaten: Math.round(eaten), left: plan.calories - Math.round(eaten) };
}

export function leftLine(l) {
  if (!l.ready) return `${l.eaten.toLocaleString('en-US')} eaten today. Weigh in to get your daily number.`;
  return l.left >= 0 ? `${l.left.toLocaleString('en-US')} left today.` : `${Math.abs(l.left).toLocaleString('en-US')} over today.`;
}

export async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function newKey() {
  const b = crypto.getRandomValues(new Uint8Array(24));
  return 'grit_' + btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
